// src/modules/app-views/leads/pages/Init/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useRef, useState } from 'react';
import { FloatButton, Table } from 'antd';
import { EyeOutlined, SwapOutlined } from '@ant-design/icons';
import { useListContext } from 'components/core/ListProvider';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import {
  nameColumn,
  statusColumn,
  distributionColumn,
  landingPageColumn,
  originColumn,
  assignmentColumn,
} from './components/tableColumns';
import DistributeLeadsModal from './modals/DistributeLeadsModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const LeadsInit = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    data,
    loading,
    tableKey,
    pagination,
    handleTableChanges,
    rowSelection,
    buildTableColumns,
    availableTableColumns,
    buildNumberLineColumn,
    buildDocumentCodeColumn,
    buildCreatedAtColumn,
    buildUpdatedAtColumn,
    buildDocumentInfoIconsColumn,
    buildActionsButtonColumn,
    buildColumnProps,
    query,
    tableComponents,
    hiddenColumns,
    isEditingTable,
    TableFeatures,
    registerFloatButtonCustomActions,
    handleEditDocument,
    refresh,
  } = useListContext();

  const storedListSettings = useZustand('module', 'listSettings');
  const storedLanguage = useZustand('theme', 'language');

  // Current folder
  const folder = storedListSettings?.filterDrawer?.folder || query?.drawer?.folder || 'inbox';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [distributeLeads, setDistributeLeads] = useState([]);
  const wasDistributedRef = useRef(false);

  // Serialize sort and search for stable dependencies
  const sortKey = JSON.stringify(query?.sort || []);
  const searchKey = JSON.stringify(query?.search || {});

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = appService.isMobile();

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleDistribute = useCallback(
    rows => {
      setDistributeLeads(rows);
      setShowDistributeModal(true);
    },
    []
  );

  const handleDistributeSuccess = useCallback(() => {
    wasDistributedRef.current = true;
  }, []);

  const handleDistributeAfterClose = useCallback(() => {
    setDistributeLeads([]);
    if (wasDistributedRef.current) {
      wasDistributedRef.current = false;
      refresh();
    }
  }, [refresh]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Register distribute float button for selected leads (inbox folder only)
  useEffect(() => {
    registerFloatButtonCustomActions?.(rows => {
      if (!rows?.length || ['archive', 'recycle'].includes(folder)) return null;
      return (
        <FloatButton
          key="distribute"
          icon={<SwapOutlined />}
          tooltip={{ title: translateX('distribute_leads'), placement: 'left' }}
          onClick={() => handleDistribute(rows)}
        />
      );
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder, handleDistribute]);

  // Build table columns
  useEffect(() => {
    buildTableColumns?.([
      buildNumberLineColumn(),

      buildDocumentCodeColumn(),

      nameColumn(buildColumnProps),

      landingPageColumn(buildColumnProps),

      originColumn(buildColumnProps),

      statusColumn(buildColumnProps),

      distributionColumn(buildColumnProps),

      assignmentColumn(buildColumnProps),

      buildCreatedAtColumn(),

      buildUpdatedAtColumn(),

      buildDocumentInfoIconsColumn(),

      buildActionsButtonColumn({
        view: false,
        edit: false,
        ...(['archive', 'recycle'].includes(folder)
          ? { fitInDropDownMenu: ['remove'] }
          : { fitInDropDownMenu: ['archive', 'remove'] }),
        // View button navigates to document page for all folders
        // Distribute button appears only in inbox/documents for leads in queue status
        prependButtons: r => {
          const buttons = [
            {
              key: 'view',
              icon: <EyeOutlined />,
              label: translateX('view'),
              onClick: () => handleEditDocument(r),
            },
          ];
          if (!['archive', 'recycle'].includes(folder) && r?.status === 'queue') {
            buttons.push({
              key: 'distribute',
              icon: <SwapOutlined />,
              label: translateX('distribute_leads'),
              onClick: () => handleDistribute([r]),
            });
          }
          return buttons;
        },
      }),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storedListSettings?.showNumberOfLine,
    sortKey,
    searchKey,
    storedLanguage,
    hiddenColumns,
    isEditingTable,
    query?.drawer?.folder,
    folder,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <TableFeatures />

      <Table
        key={tableKey}
        scroll={{ x: 'max-content' }}
        bordered
        rowKey="_id"
        size={isMobile ? 'small' : 'large'}
        columns={availableTableColumns}
        dataSource={data || []}
        pagination={pagination}
        className="with-checkbox"
        loading={loading}
        onChange={handleTableChanges}
        rowSelection={rowSelection}
        components={tableComponents}
      />

      <DistributeLeadsModal
        show={showDistributeModal}
        setShow={setShowDistributeModal}
        leads={distributeLeads}
        afterClose={handleDistributeAfterClose}
        onSuccess={handleDistributeSuccess}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default LeadsInit;
